import hazardProfileReducer from "./hazardProfileReducer";
import premDeductReducer from "./premDeductReducer";
import * as _ from "lodash";
import * as actionTypes from "store/actions/input/exposure/exposureActionTypes";
import * as persistenceActionTypes from "store/actions/persistence/persistenceActionTypes";

const exposureReducer = (state, action) => {
  if (state === undefined) {
    state = {};
  }
  {
    const hazardProfile = hazardProfileReducer(state.hazardProfile, action);
    const premDeduct = premDeductReducer(state.premDeduct, action);
    if (
      hazardProfile !== state.hazardProfile ||
      premDeduct !== state.premDeduct
    ) {
      state = {
        ...state,
        hazardProfile,
        premDeduct,
      };
    }
  }

  switch (action.type) {
    case persistenceActionTypes.LOAD_SUBMISSION_STARTED:
      return {};
    case persistenceActionTypes.LOAD_SUBMISSION_SUCCEEDED:
      return {
        ...action?.payload?.data?.state?.input?.exposure,
      };
    case actionTypes.UPDATE_EXPOSURE_VALUE:
      return {
        ...state,
        data: {
          ...state.data,
          [action.payload.year]: {
            ...(state.data?.[action.payload.year] || {}),
            [action.payload.key]: action.payload.value,
          },
        },
      };
    case actionTypes.UPDATE_EXPOSURE_NOTE:
      return {
        ...state,
        note: action.payload,
      };
    case actionTypes.ADD_EXTRA_EXPOSURE_METRIC:
      return {
        ...state,
        extraKeys: [...(state.extraKeys ?? []), action.payload],
      };
    case actionTypes.DELETE_EXTRA_EXPOSURE_METRIC:
      return {
        ...state,
        extraKeys: state.extraKeys?.filter((_) => _ !== action.payload),
        primaryExposure:
          state.primaryExposure === action.payload
            ? null
            : state.primaryExposure,
      };
    case actionTypes.HIDE_TEMPLATE_EXPOSURE_METRIC:
      return {
        ...state,
        removedKeys: [...(state.removedKeys ?? []), action.payload],
        primaryExposure:
          state.primaryExposure === action.payload
            ? null
            : state.primaryExposure,
      };
    case actionTypes.UNHIDE_TEMPLATE_EXPOSURE_METRIC:
      return {
        ...state,
        removedKeys: state.removedKeys?.filter((_) => _ !== action.payload),
      };
    case actionTypes.UPDATE_CUSTOM_MEASURE_NAME:
    case actionTypes.UPDATE_CUSTOM_MEASURE_UNIT:
      if (
        state.customKeys?.some(
          (customKey) => customKey.key === action.payload.key
        )
      ) {
        return {
          ...state,
          customKeys: state.customKeys
            ?.map((customKey) =>
              action.payload.key === customKey.key
                ? {
                    ...customKey,
                    ...action.payload,
                  }
                : customKey
            )
            ?.filter((customKey) => !!customKey.name || !!customKey.units),
        };
      } else {
        return {
          ...state,
          customKeys: [
            ...(state.customKeys ?? []),
            { ...action.payload },
          ].filter((customKey) => !!customKey.name || !!customKey.units),
        };
      }
    case actionTypes.DELETE_CUSTOM_MEASURE:
      return {
        ...state,
        customKeys: state.customKeys?.map((item) => {
          if (item.key === action.payload) {
            return { ...item, deleted: true };
          }
          return item;
        }),
      };

    case actionTypes.UPDATE_EXPOSURE_COMMENT:
      return {
        ...state,
        comments: {
          ...state.comments,
          [action.payload.key]: action.payload.value,
        },
      };

    case actionTypes.SHOW_ALL_EXPOSURE_COMMENTS:
      return {
        ...state,
        commentVisibility: {
          defaultVisible: true,
          exceptKeys: [],
        },
      };

    case actionTypes.HIDE_ALL_EXPOSURE_COMMENTS:
      return {
        ...state,
        commentVisibility: {
          defaultVisible: false,
          exceptKeys: [],
        },
      };

    case actionTypes.SHOW_EXPOSURE_COMMENT:
      return {
        ...state,
        commentVisibility: {
          ...state.commentVisibility,
          exceptKeys: state.commentVisibility?.defaultVisible
            ? _.without(
                state.commentVisibility?.exceptKeys ?? [],
                action.payload.key
              )
            : _.union(state.commentVisibility?.exceptKeys ?? [], [
                action.payload.key,
              ]),
        },
      };

    case actionTypes.HIDE_EXPOSURE_COMMENT:
      return {
        ...state,
        commentVisibility: {
          ...state.commentVisibility,
          exceptKeys: state.commentVisibility?.defaultVisible
            ? _.union(state.commentVisibility?.exceptKeys ?? [], [
                action.payload.key,
              ])
            : _.without(
                state.commentVisibility?.exceptKeys ?? [],
                action.payload.key
              ),
        },
      };
    case actionTypes.ADD_EXPOSURE_YEAR:
      const newData = {
        ...state.data,
      };
      newData[action.payload] = {};
      return {
        ...state,
        data: newData,
      };
    case actionTypes.UPDATE_PRIMARY_EXPOSURE:
      return {
        ...state,
        primaryExposure: action.payload,
      };
    case actionTypes.REORDER_EXPOSURE_ROWS: {
      const { fromIndex, toIndex, rowKeys } = action.payload;
      const currentOrder =
        state.exposureOrder && state.exposureOrder.length > 0
          ? state.exposureOrder
          : rowKeys;
      const newOrder = [...currentOrder];
      const [movedItem] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, movedItem);
      return {
        ...state,
        exposureOrder: newOrder,
      };
    }
    case actionTypes.REMAP_CUSTOM_MEASURE_KEYS: {
      const mapping = action.payload ?? {};
      const entries = Object.entries(mapping).filter(
        ([from, to]) => !!from && !!to && from !== to
      );

      if (entries.length === 0) {
        return state;
      }

      const mapKey = (key) => (mapping?.[key] ? mapping[key] : key);

      const remapRecordKeys = (record) => {
        if (!record) {
          return record;
        }
        const next = { ...record };
        for (const [from, to] of entries) {
          if (Object.prototype.hasOwnProperty.call(next, from)) {
            const fromVal = next[from];
            const hasTo = Object.prototype.hasOwnProperty.call(next, to);
            if (hasTo) {
              delete next[to];
            }
            next[to] = fromVal;
            delete next[from];
          }
        }
        return next;
      };

      const remapData = Object.fromEntries(
        Object.entries(state.data ?? {}).map(([year, values]) => [
          year,
          remapRecordKeys(values),
        ])
      );

      return {
        ...state,
        data: remapData,
        comments: remapRecordKeys(state.comments),
        customKeys: (state.customKeys ?? []).map((item) => ({
          ...item,
          key: mapKey(item.key),
        })),
        primaryExposure: mapKey(state.primaryExposure),
        exposureOrder: (state.exposureOrder ?? []).map((key) => mapKey(key)),
      };
    }
    default:
      return state;
  }
};

export default exposureReducer;
